/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
// The keyboard listener is added to the document and while this is presentational, the contents are not.
import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

const UX_DISRUPT_CLASSNAME = 'ux-disrupt';

/**
 * Toggles the data-inert attribute, for managing multiple possible disruptions
 *
 * @param {Element} parent - The parent of the component
 * @param {Boolean} inert - Determines if the inert attribute should be added or removed
 */
function manageInert(parent, inert) {
  const amount = (Number(inert) * 2) - 1;
  const value = Number(parent.getAttribute('data-inert')) + amount;
  if (value) {
    parent.setAttribute('data-inert', value);
  } else {
    parent.removeAttribute('data-inert');
  }
}

/**
 * Create a <Disrupt/> component
 *
 * @param {Object} props - Properties to inform the component
 * @param {Elements} [props.children] - Given children for the content container
 * @param {Function} [props.onClose] - Callback which fires when the user attempts to leave
 * @param {Function} [prop.onEventBehind] - Callback which fires when an event occurs behind the component
 * @param {Element} [prop.portalElem] - The element to attach the component
 * @param {String} [prop.strategy] - The method that the backdrop is attached
 * @param {Boolean} [props.stretch] - Occupy the full width of the container
 * @returns {Component} - The Disrupt component
 */
function Disrupt(props) {
  const {
    children,
    onClose,
    onEventBehind,
    portalElem,
    strategy,
    stretch
  } = props;

  const style = { position: strategy || 'absolute' };
  const componentRef = useRef(null);

  useEffect(function effect() {
    // Determine the element where the component is attached
    const parent = portalElem || componentRef.current.parentElement;

    // Function for catching events that occur behind the disruption
    function eventBehind(ev) {
      const insideParent = parent.contains(ev.target);
      const insideComponent = componentRef.current.contains(ev.target);
      if (insideParent && !insideComponent) onEventBehind(ev);
    }

    // Function for catching the Esc key
    function escKey(ev) { ev.key === 'Escape' && onClose(ev); }

    // Set a data-attribute on the parent that _shouldn't_ be clobbered by re-renders
    manageInert(parent, true);

    // Listen to all the things
    typeof onEventBehind === 'function' && parent.addEventListener('click', eventBehind);
    typeof onEventBehind === 'function' && parent.addEventListener('focusin', eventBehind);
    typeof onClose === 'function' && document.addEventListener('keydown', escKey);
    return function teardown() {

      // Teardown
      manageInert(parent, false);
      parent.removeEventListener('click', eventBehind);
      parent.removeEventListener('focusin', eventBehind);
      document.removeEventListener('keydown', escKey);
    };
  }, [portalElem, componentRef.current]);

  const classNames = [`${UX_DISRUPT_CLASSNAME}-content`];

  if (stretch) {
    classNames.push(`${UX_DISRUPT_CLASSNAME}--stretch`);
  }

  /**
   * handleOutsideClick - only fires the onClose event when the click occurs outside of container
   * @param {React.SyntheticEvent} ev - onMouseDown event
   */
  function handleOutsideClick(ev) {
    if (componentRef.current !== ev.target && componentRef.current.contains(ev.target)) {
      return;
    }

    onClose(ev);
  }

  const component = (
    <div className={ `${UX_DISRUPT_CLASSNAME}-backdrop` } ref={ componentRef } onMouseDown={ handleOutsideClick } style={ style }>
      <div className={ classNames.join(' ') }>
        { children }
      </div>
    </div>
  );

  return portalElem ? ReactDOM.createPortal(component, portalElem) : component;
}

Disrupt.propTypes = {
  /** Children to render within */
  children: PropTypes.node,
  /** Callback that fires when the user attempts to leave the component */
  onClose: PropTypes.func,
  /** Callback that fires when an event occurs behind the component */
  onEventBehind: PropTypes.func,
  /** Element to attach the component to, use document.body for "modal" behavior */
  portalElem: PropTypes.object,
  /** Method to attach the backdrop */
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  /** Stretches the width of the container */
  stretch: PropTypes.bool
};

export default Disrupt;
